/**
 * The Profile page's model: the facts a person can set about themselves
 * (written to the Profile tab with source "user") and the things they own or
 * owe that have no statements (the Manual tab). The Sheet stays the only
 * store; this file decides which rows a save writes. See ARCHITECTURE.md,
 * Profile page.
 */
import { formatTimestamp, isIsoDate } from './dates'
import type { Database, Row } from './google/sheets'

export interface Field {
  key: string
  label: string
  input: 'text' | 'number' | 'date' | 'textarea' | 'select'
  options?: string[]
  placeholder?: string
  help?: string
}

export interface FieldGroup {
  title: string
  fields: Field[]
}

/** The facts that change the advice most, in the order the page asks for them. */
export const PROFILE_FIELDS: FieldGroup[] = [
  {
    title: 'About you',
    fields: [
      { key: 'birth_year', label: 'Your birth year', input: 'number', placeholder: 'e.g. 1975' },
      { key: 'spouse_birth_year', label: 'Spouse or partner’s birth year', input: 'number' },
      { key: 'filing_status', label: 'Tax filing status', input: 'select', options: ['single', 'married filing jointly', 'married filing separately', 'head of household'] },
      { key: 'state', label: 'State you live in', input: 'text', placeholder: 'e.g. OR' },
      { key: 'dependents', label: 'Dependents', input: 'text', placeholder: 'e.g. 2 children, ages 8 and 11' },
      { key: 'hsa_eligible', label: 'On a high-deductible health plan (HSA-eligible)?', input: 'select', options: ['yes, self-only', 'yes, family', 'no'] },
    ],
  },
  {
    title: 'Income and tax',
    fields: [
      { key: 'income', label: 'Household income', input: 'text', placeholder: 'e.g. salary 240000 + bonus about 40000' },
      { key: 'marginal_tax_bracket', label: 'Federal tax bracket', input: 'select', options: ['10%', '12%', '22%', '24%', '32%', '35%', '37%'] },
      { key: 'agi_last_year', label: 'Adjusted gross income last year', input: 'number', help: 'Line 11 of Form 1040' },
    ],
  },
  {
    title: 'Goals',
    fields: [
      { key: 'retirement_year', label: 'Year you plan to retire', input: 'number', placeholder: 'e.g. 2035' },
      { key: 'retirement_spending', label: 'Yearly spending in retirement, today’s dollars', input: 'number', placeholder: 'e.g. 120000' },
      { key: 'emergency_fund_target_months', label: 'Emergency fund target, months of spending', input: 'number', placeholder: 'e.g. 6' },
      { key: 'big_expenses', label: 'Large expenses ahead', input: 'textarea', placeholder: 'e.g. Kitchen remodel 2027, about 80000; college 2031–2035' },
    ],
  },
  {
    title: 'Investing',
    fields: [
      { key: 'risk_tolerance', label: 'Risk tolerance', input: 'select', options: ['conservative', 'moderate', 'growth', 'aggressive'] },
      { key: 'target_allocation', label: 'Target stock/bond mix, if you have one', input: 'text', placeholder: 'e.g. 60/40' },
      { key: 'investing_preferences', label: 'Preferences', input: 'textarea', placeholder: 'e.g. Index funds only; no individual stocks' },
    ],
  },
  {
    title: 'Inherited IRA',
    fields: [
      { key: 'inherited_ira_owner_death_date', label: 'Original owner’s date of death', input: 'date', help: 'Sets the 10-year deadline' },
      { key: 'inherited_ira_owner_birth_year', label: 'Original owner’s birth year', input: 'number', help: 'Decides whether yearly withdrawals are required' },
      { key: 'inherited_ira_relationship', label: 'Your relationship to them', input: 'select', options: ['spouse', 'child', 'grandchild', 'sibling', 'other relative', 'not related', 'trust'] },
    ],
  },
  {
    title: 'Protection and estate',
    fields: [
      { key: 'umbrella_policy', label: 'Umbrella liability policy', input: 'text', placeholder: 'e.g. none, or 2M with State Farm' },
      { key: 'life_insurance', label: 'Life insurance', input: 'text', placeholder: 'e.g. term 1.5M, expires 2038' },
      { key: 'disability_insurance', label: 'Disability insurance', input: 'text', placeholder: 'e.g. through employer, 60% of salary' },
      { key: 'will_trust', label: 'Will or trust', input: 'text', placeholder: 'e.g. revocable trust, updated 2019-03-01' },
      { key: 'beneficiaries_reviewed', label: 'Beneficiaries last reviewed', input: 'date' },
    ],
  },
  {
    title: 'Business',
    fields: [{ key: 'business_accounts', label: 'Business accounts and how to treat them', input: 'textarea', placeholder: 'e.g. ODH LLC (Ally ··7626) is a business; leave it out of household spending' }],
  },
]

/** Date fields must be YYYY-MM-DD; returns the labels of any that are not. */
export function badDates(values: Record<string, string>): string[] {
  return PROFILE_FIELDS.flatMap((g) => g.fields)
    .filter((f) => f.input === 'date' && values[f.key]?.trim() && !isIsoDate(values[f.key].trim()))
    .map((f) => f.label)
}

const FIELD_KEYS = new Set(PROFILE_FIELDS.flatMap((g) => g.fields.map((f) => f.key)))
export const isListedField = (key: string) => FIELD_KEYS.has(key)

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v).trim())
export const isInferredRow = (r: Row) => str(r.source).toLowerCase() === 'inferred'

/** The person's own values, by key. */
export function userValues(rows: Row[]): Record<string, string> {
  return Object.fromEntries(rows.filter((r) => !isInferredRow(r) && str(r.key)).map((r) => [str(r.key), str(r.value)]))
}

/**
 * The Profile rows after a save. `values` is every key the page shows: a
 * non-empty value becomes (or updates) the person's row and replaces any
 * inferred row for that key; an empty value removes the person's row. Rows
 * for keys the page did not show, and inferred rows for other keys, are kept.
 */
export function mergeProfile(rows: Row[], values: Record<string, string>, now: Date): Row[] {
  const stamp = formatTimestamp(now)
  const set = new Map(Object.entries(values).map(([k, v]) => [k.trim(), v.trim()]))
  const kept = rows.filter((r) => {
    const key = str(r.key)
    if (!set.has(key)) return true
    // A key the page owns: the person's value (or its absence) decides.
    return isInferredRow(r) && !set.get(key)
  })
  const changed: Row[] = []
  for (const [key, value] of set) {
    if (!key || !value) continue
    const before = rows.find((r) => str(r.key) === key && !isInferredRow(r))
    changed.push({
      key,
      value,
      source: 'user',
      basis: before && str(before.value) === value ? before.basis ?? 'Entered in Finance Buddy' : 'Entered in Finance Buddy',
      updated_at: before && str(before.value) === value ? before.updated_at ?? stamp : stamp,
    })
  }
  return [...kept, ...changed]
}

export const MANUAL_ASSET_CATEGORIES = ['home', 'real estate', 'vehicle', '401k', 'ira', 'pension', 'brokerage', 'crypto', 'cash', 'hsa', '529', 'business', 'other']
export const MANUAL_DEBT_CATEGORIES = ['mortgage', 'auto loan', 'student loan', 'heloc', 'personal loan', 'other']

export interface ManualItem {
  name: string
  type: 'asset' | 'debt'
  category: string
  value: string
  as_of: string
  rate: string
  notes: string
  /** The row as read, so columns the person added in the Sheet survive a save. */
  row?: Row
}

/** A blank row added on the page and left empty: not saved, and not an error. */
export const isBlankItem = (i: ManualItem) => !i.name.trim() && !i.value.trim()

export function toItems(rows: Row[]): ManualItem[] {
  return rows.map((r) => ({
    name: str(r.name),
    // As the analysis reads it: a debt by its type, or by a negative value.
    type: ['debt', 'liability', 'loan', 'owed'].includes(str(r.type).trim().toLowerCase()) || Number(r.value) < 0 ? 'debt' : 'asset',
    category: str(r.category),
    value: str(r.value),
    as_of: str(r.as_of),
    rate: str(r.rate),
    notes: str(r.notes),
    row: r,
  }))
}

/** Problems that would stop an item counting; an empty list means it can be saved. */
export function itemProblems(item: ManualItem): string[] {
  const problems: string[] = []
  if (!item.name.trim()) problems.push('needs a name')
  if (item.value.trim() === '' || !Number.isFinite(Number(item.value))) problems.push('needs a value in dollars')
  if (item.rate.trim() !== '' && !Number.isFinite(Number(item.rate))) problems.push('rate must be a number')
  if (item.as_of.trim() !== '' && !isIsoDate(item.as_of.trim())) problems.push('date must be YYYY-MM-DD')
  return problems
}

export function toRows(items: ManualItem[]): Row[] {
  return items.map((i) => ({
    ...i.row,
    name: i.name.trim(),
    type: i.type,
    category: i.category.trim(),
    value: Math.abs(Number(i.value)),
    as_of: i.as_of || null,
    rate: i.rate.trim() === '' ? null : Number(i.rate),
    notes: i.notes.trim(),
  }))
}

export async function loadDetails(db: Database): Promise<{ profile: Row[]; manual: Row[] }> {
  const [profile, manual] = await Promise.all([db.read('Profile'), db.read('Manual')])
  return { profile: profile.rows, manual: manual.rows }
}

export async function saveProfile(db: Database, values: Record<string, string>): Promise<Row[]> {
  const table = await db.modify('Profile', (t) => {
    t.rows = mergeProfile(t.rows, values, new Date())
  })
  return table.rows
}

export async function saveManual(db: Database, items: ManualItem[]): Promise<void> {
  await db.modify('Manual', (t) => {
    t.rows = toRows(items.filter((i) => !isBlankItem(i)))
  })
}
